import React, { useState, useEffect } from 'react';

const COR = {
  azul:    '#003399', azulBg:  '#eff6ff',
  borda:   '#e8eaed', texto:   '#1a1a2e', muted: '#888',
  branco:  '#fff',    cinzaBg: '#fafafa',
  verde:   '#1D9E75',
  roxo:    '#7c3aed', roxoBg:  '#f3e8ff',
};

const formatHoras = (n) => {
  const v = parseFloat(n);
  if (isNaN(v)) return '0,00';
  return v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

// ════════════════════════════════════════════════════════════════════════════
// SelecionarItensModal — sub-modal pra escolher quais itens da quantificação
// ficam atribuídos a um analista. Itens já alocados a OUTROS analistas
// aparecem desabilitados (com indicador de quem é o dono atual).
// ────────────────────────────────────────────────────────────────────────────
// Props:
//   aberto          : boolean
//   itensQuantif    : array de itens da quantificação (com id, subtopico,
//                     subtopico_descricao, horas, tipo_descricao, tipo_cor)
//   itensSelecionados: array de IDs já marcados (do analista atual)
//   atribuicoesPorItem: Map<itemId, { analistaId, analistaNome }>
//                      itens que estão atribuídos a OUTROS analistas
//   analistaAtualId : id do analista sendo editado (pode ser null = novo)
//   onCancelar      : callback
//   onConfirmar(idsArray)
// ════════════════════════════════════════════════════════════════════════════
function SelecionarItensModal({
  aberto, itensQuantif = [], itensSelecionados = [],
  atribuicoesPorItem = {}, analistaAtualId,
  onCancelar, onConfirmar,
}) {
  const [selecionados, setSelecionados] = useState(new Set(itensSelecionados));

  useEffect(() => {
    if (aberto) setSelecionados(new Set(itensSelecionados));
  }, [aberto, itensSelecionados]);

  if (!aberto) return null;

  const toggleItem = (item) => {
    // Não permite marcar/desmarcar item que está atribuído a outro analista
    const atrib = atribuicoesPorItem[item.id];
    if (atrib && atrib.analistaId !== analistaAtualId) return;

    setSelecionados(s => {
      const novo = new Set(s);
      novo.has(item.id) ? novo.delete(item.id) : novo.add(item.id);
      return novo;
    });
  };

  const marcarTodosLivres = () => {
    const livres = itensQuantif.filter(i => {
      const atrib = atribuicoesPorItem[i.id];
      return !atrib || atrib.analistaId === analistaAtualId;
    });
    setSelecionados(new Set(livres.map(i => i.id)));
  };
  const desmarcarTodos = () => setSelecionados(new Set());

  // Soma horas dos selecionados
  const horasSelecionadas = itensQuantif
    .filter(i => selecionados.has(i.id))
    .reduce((s, i) => s + (parseFloat(i.horas) || 0), 0);

  // Z-index acima do PlanejamentoModal (10000) e do AnalistaModal (10001)
  const Z_OVERLAY = 10010;
  const Z_MODAL   = 10011;

  return (
    <>
      <div onClick={onCancelar} style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: Z_OVERLAY,
      }} />
      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '92%', maxWidth: '720px', maxHeight: '85vh',
        backgroundColor: COR.branco, borderRadius: '8px',
        boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
        display: 'flex', flexDirection: 'column',
        zIndex: Z_MODAL, overflow: 'hidden',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <div style={{
          padding: '14px 18px', borderBottom: `1px solid ${COR.borda}`,
          backgroundColor: COR.roxo, color: COR.branco,
          display: 'flex', alignItems: 'center', gap: '10px',
        }}>
          <span style={{ fontSize: '17px' }}>📌</span>
          <span style={{ fontSize: '14px', fontWeight: '600', flex: 1 }}>
            Selecionar Itens do Analista
          </span>
          <button onClick={onCancelar} style={{
            border: 'none', background: 'transparent', color: COR.branco,
            fontSize: '20px', cursor: 'pointer',
          }}>×</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {/* Toolbar */}
          <div style={{
            padding: '10px 14px', borderBottom: `1px solid ${COR.borda}`,
            display: 'flex', alignItems: 'center', gap: '8px',
            backgroundColor: COR.cinzaBg, fontSize: '12px',
          }}>
            <span style={{ color: COR.muted }}>
              {selecionados.size} de {itensQuantif.length} item(ns) · {formatHoras(horasSelecionadas)}h
            </span>
            <div style={{ flex: 1 }} />
            <button onClick={marcarTodosLivres} style={miniBtnStyle}>
              Marcar livres
            </button>
            <button onClick={desmarcarTodos} style={miniBtnStyle}>
              Desmarcar tudo
            </button>
          </div>

          {/* Lista */}
          {itensQuantif.length === 0 ? (
            <div style={{
              padding: '30px', textAlign: 'center', color: COR.muted, fontSize: '13px',
            }}>
              Este projeto não tem itens de quantificação.
            </div>
          ) : (
            <div>
              {itensQuantif.map(item => {
                const atrib = atribuicoesPorItem[item.id];
                const blocked = atrib && atrib.analistaId !== analistaAtualId;
                const checked = selecionados.has(item.id);
                return (
                  <label
                    key={item.id}
                    onClick={() => !blocked && toggleItem(item)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '10px 14px',
                      borderBottom: `1px solid ${COR.borda}`,
                      cursor: blocked ? 'not-allowed' : 'pointer',
                      backgroundColor: blocked ? COR.cinzaBg : (checked ? COR.azulBg : COR.branco),
                      opacity: blocked ? 0.6 : 1,
                      userSelect: 'none',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => {}}
                      disabled={blocked}
                      style={{ cursor: blocked ? 'not-allowed' : 'pointer' }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '13px', color: COR.texto, fontWeight: '500' }}>
                        {item.subtopico || item.topico} — {item.subtopico_descricao}
                      </div>
                      <div style={{
                        fontSize: '11px', color: COR.muted, marginTop: '2px',
                        display: 'flex', gap: '8px', flexWrap: 'wrap',
                      }}>
                        {item.tipo_descricao && (
                          <span style={{
                            display: 'inline-block', padding: '1px 6px',
                            borderRadius: '8px', fontSize: '10px', fontWeight: '600',
                            backgroundColor: (item.tipo_cor || '#888') + '22',
                            color: item.tipo_cor || '#888',
                          }}>{item.tipo_sigla || item.tipo_descricao}</span>
                        )}
                        <span>{formatHoras(item.horas)}h</span>
                        {blocked && (
                          <span style={{ color: COR.muted, fontStyle: 'italic' }}>
                            🔒 Já atribuído a {atrib.analistaNome}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 18px', borderTop: `1px solid ${COR.borda}`,
          display: 'flex', justifyContent: 'flex-end', gap: '8px',
          backgroundColor: COR.cinzaBg,
        }}>
          <button onClick={onCancelar} style={{
            padding: '8px 18px', fontSize: '13px', fontWeight: '600',
            border: `1px solid ${COR.borda}`, borderRadius: '5px',
            backgroundColor: COR.branco, color: COR.texto, cursor: 'pointer',
          }}>Cancelar</button>
          <button onClick={() => onConfirmar(Array.from(selecionados))} style={{
            padding: '8px 22px', fontSize: '13px', fontWeight: '600',
            border: 'none', borderRadius: '5px',
            backgroundColor: COR.azul, color: COR.branco, cursor: 'pointer',
          }}>Confirmar</button>
        </div>
      </div>
    </>
  );
}

const miniBtnStyle = {
  padding: '4px 10px', fontSize: '11px', fontWeight: '600',
  border: `1px solid ${COR.borda}`, borderRadius: '4px',
  backgroundColor: COR.branco, color: COR.azul, cursor: 'pointer',
};

export default SelecionarItensModal;
